import React, { useState, useEffect, useCallback } from "react";
import { collection, doc, getDoc, getDocs, orderBy, query, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../firebase/firebase-config";
import defaultHeroImage from "../assets/images/hero.jpg";
import defaultContactImage from "../assets/images/LetsConnect.jpeg";

const SEED_CREDENTIALS = [
  "5+ Years' Legal Experience",
  "LLM (Distinction) · Queen Mary, London",
  "Clarity · Strategy · Growth",
];

export default function HeroPanel() {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [images, setImages] = useState({ heroImage: "", contactImage: "" });
  const [uploading, setUploading] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [imageUrls, setImageUrls] = useState({ heroImage: "", contactImage: "" });
  const [pickerFor, setPickerFor] = useState("");
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "hero_config", "main"));
      const data = snap.exists() ? snap.data() : {};
      setCredentials(Array.isArray(data.credentials) ? data.credentials : SEED_CREDENTIALS);
      setImages({ heroImage: data.heroImage || "", contactImage: data.contactImage || "" });
      setImageUrls({ heroImage: data.heroImage || "", contactImage: data.contactImage || "" });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persist = async (next) => {
    setSaving(true);
    try {
      await setDoc(doc(db, "hero_config", "main"), { credentials: next }, { merge: true });
      setCredentials(next);
    } finally { setSaving(false); }
  };

  const addCredential = async () => {
    const text = draft.trim();
    if (!text) return;
    await persist([...credentials, text]);
    setDraft("");
  };

  const startEdit = (i) => { setEditingIndex(i); setEditingText(credentials[i]); };
  const saveEdit = async () => {
    const text = editingText.trim();
    if (!text) return;
    const next = credentials.map((c, i) => (i === editingIndex ? text : c));
    await persist(next);
    setEditingIndex(null);
  };

  const removeCredential = async (i) => {
    if (!window.confirm("Remove this credential?")) return;
    await persist(credentials.filter((_, idx) => idx !== i));
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= credentials.length) return;
    const next = [...credentials];
    [next[i], next[j]] = [next[j], next[i]];
    await persist(next);
  };

  const uploadImage = (kind, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setMessage("Please choose an image file.");
      return;
    }
    setUploading(kind);
    setUploadProgress(0);
    setMessage("");
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const storageRef = ref(storage, `site-images/${kind}-${Date.now()}.${extension}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on("state_changed", snap => {
      setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
    }, () => {
      setUploading("");
      setMessage("Upload failed. Please try again.");
    }, async () => {
      try {
        const url = await getDownloadURL(task.snapshot.ref);
        await setDoc(doc(db, "hero_config", "main"), { [kind]: url }, { merge: true });
        setImages(current => ({ ...current, [kind]: url }));
        setImageUrls(current => ({ ...current, [kind]: url }));
        setMessage(`${kind === "heroImage" ? "Hero" : "Contact"} image updated.`);
      } catch (_) {
        setMessage("The image uploaded, but saving it failed. Please try again.");
      } finally {
        setUploading("");
        setUploadProgress(0);
      }
    });
  };

  const saveImageUrl = async (kind) => {
    const url = imageUrls[kind].trim();
    if (!/^https?:\/\//i.test(url)) {
      setMessage("Please paste a valid image URL beginning with http:// or https://.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await setDoc(doc(db, "hero_config", "main"), { [kind]: url }, { merge: true });
      setImages(current => ({ ...current, [kind]: url }));
      setMessage(`${kind === "heroImage" ? "Hero" : "Contact"} image URL saved.`);
    } catch (_) {
      setMessage("Saving the image URL failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const openLibrary = async (kind) => {
    setPickerFor(kind);
    setLibrarySearch("");
    if (library.length) return;
    setLibraryLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "media_library"), orderBy("uploadedAt", "desc")));
      setLibrary(snap.docs.map(item => ({ id: item.id, ...item.data() })).filter(item => item.url));
    } catch (_) {
      setMessage("Could not load the Media Library. Please try again.");
      setPickerFor("");
    } finally {
      setLibraryLoading(false);
    }
  };

  const chooseLibraryImage = async (item) => {
    const kind = pickerFor;
    if (!kind) return;
    setSaving(true);
    try {
      await setDoc(doc(db, "hero_config", "main"), { [kind]: item.url }, { merge: true });
      setImages(current => ({ ...current, [kind]: item.url }));
      setImageUrls(current => ({ ...current, [kind]: item.url }));
      setMessage(`${kind === "heroImage" ? "Hero" : "Contact"} image selected from Media Library.`);
      setPickerFor("");
    } catch (_) {
      setMessage("Selecting the library image failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filteredLibrary = library.filter(item => {
    const haystack = `${item.filename || ""} ${item.customLabel || ""} ${item.slot || ""}`.toLowerCase();
    return haystack.includes(librarySearch.trim().toLowerCase());
  });

  if (loading && !credentials.length) return <div className="adm-panel-loading">Loading…</div>;

  return (
    <section className="adm-panel">
      <div className="adm-panel-head">
        <div>
          <h2>🏅 Hero Credentials — Homepage Banner</h2>
          <p>Edit the credential pills shown under your name on the homepage (e.g. "5+ Years' Legal Experience")</p>
        </div>
      </div>

      <div className="adm-tab-content">
        <h3 style={{ margin: "0 0 .35rem" }}>Homepage photos</h3>
        <p style={{ margin: "0 0 1rem", color: "#76675d" }}>Change the portrait in the main hero and the photo beside the contact form.</p>
        <div className="adm-hero-image-grid">
          {[
            { key: "heroImage", label: "Hero image", fallback: defaultHeroImage },
            { key: "contactImage", label: "Contact image", fallback: defaultContactImage },
          ].map(item => (
            <div key={item.key} className="adm-hero-image-card">
              <div className="adm-hero-image-title"><strong>{item.label}</strong><span>LIVE</span></div>
              <div className="adm-hero-image-preview">
                <img src={images[item.key] || item.fallback} alt={`${item.label} preview`} />
                <div className="adm-hero-image-preview-shade"><span>Current selection</span></div>
              </div>
              <button className="adm-hero-library-btn" onClick={() => openLibrary(item.key)} disabled={saving || !!uploading}>
                <span className="adm-hero-library-icon">▦</span>
                <span><strong>Choose from Media Library</strong><small>Browse every uploaded visual</small></span>
                <b>→</b>
              </button>
              <div className="adm-hero-or"><span>or</span></div>
              <label className="adm-btn" style={{ display: "inline-flex", cursor: uploading ? "wait" : "pointer" }}>
                {uploading === item.key ? `Uploading ${uploadProgress}%` : `Upload a new ${item.label.toLowerCase()}`}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden disabled={!!uploading} onChange={e => { uploadImage(item.key, e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              <details className="adm-hero-url-details">
                <summary>Use an image URL instead</summary>
              <div style={{ display: "flex", gap: ".5rem", alignItems: "stretch" }}>
                <input
                  type="url"
                  value={imageUrls[item.key]}
                  onChange={e => setImageUrls(current => ({ ...current, [item.key]: e.target.value }))}
                  placeholder="https://…"
                  aria-label={`${item.label} URL`}
                  style={{ flex: 1, minWidth: 0, padding: ".58rem .7rem", border: "1px solid #d9c8b8", borderRadius: 6 }}
                  onKeyDown={e => { if (e.key === "Enter") saveImageUrl(item.key); }}
                />
                <button className="adm-btn" onClick={() => saveImageUrl(item.key)} disabled={saving || !!uploading || !imageUrls[item.key].trim()}>Use URL</button>
              </div>
              </details>
            </div>
          ))}
        </div>
        {message && <p role="status" style={{ margin: "-.75rem 0 1.5rem", color: /updated|saved/.test(message) ? "#24704a" : "#a33a2b" }}>{message}</p>}

        <h3 style={{ margin: "0 0 .85rem" }}>Hero credentials</h3>
        <div className="adm-panel-toolbar">
          <input
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="e.g. Qualified Solicitor · England & Wales"
            style={{ flex: 1, minWidth: 220, padding: ".6rem .8rem", border: "1px solid #d9c8b8", borderRadius: 6 }}
            onKeyDown={e => { if (e.key === "Enter") addCredential(); }}
          />
          <button className="adm-btn adm-btn-primary" onClick={addCredential} disabled={saving || !draft.trim()}>
            + Add credential
          </button>
          <span className="adm-count-badge">{credentials.length} shown</span>
        </div>

        <div className="adm-list">
          {credentials.length === 0 && <p className="adm-empty">No credentials yet — add one above.</p>}
          {credentials.map((c, i) => (
            <div key={i} className="adm-list-row" style={{ "--row-color": "var(--copper)" }}>
              {editingIndex === i ? (
                <div className="adm-list-row-left" style={{ flex: 1 }}>
                  <input
                    type="text"
                    value={editingText}
                    onChange={e => setEditingText(e.target.value)}
                    style={{ flex: 1, padding: ".5rem .7rem", border: "1px solid #d9c8b8", borderRadius: 6 }}
                    onKeyDown={e => { if (e.key === "Enter") saveEdit(); }}
                    autoFocus
                  />
                </div>
              ) : (
                <div className="adm-list-row-left">
                  <div className="adm-list-order">{i + 1}</div>
                  <div className="adm-list-title">{c}</div>
                </div>
              )}
              <div className="adm-list-actions">
                {editingIndex === i ? (
                  <>
                    <button className="adm-btn adm-btn-sm adm-btn-primary" onClick={saveEdit} disabled={saving}>Save</button>
                    <button className="adm-btn adm-btn-sm" onClick={() => setEditingIndex(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button className="adm-btn adm-btn-sm" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
                    <button className="adm-btn adm-btn-sm" onClick={() => move(i, 1)} disabled={i === credentials.length - 1}>↓</button>
                    <button className="adm-btn adm-btn-sm" onClick={() => startEdit(i)}>Edit</button>
                    <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => removeCredential(i)}>Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {pickerFor && (
        <div className="adm-asset-picker-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) setPickerFor(""); }}>
          <div className="adm-asset-picker" role="dialog" aria-modal="true" aria-labelledby="asset-picker-title">
            <div className="adm-asset-picker-head">
              <div>
                <span className="adm-asset-picker-kicker">PP VISUAL ARCHIVE</span>
                <h3 id="asset-picker-title">Choose your {pickerFor === "heroImage" ? "hero" : "contact"} image</h3>
                <p>Select one of your uploaded visuals. It will go live immediately.</p>
              </div>
              <button className="adm-asset-picker-close" onClick={() => setPickerFor("")} aria-label="Close">×</button>
            </div>
            <div className="adm-asset-picker-tools">
              <span>⌕</span>
              <input type="search" value={librarySearch} onChange={e => setLibrarySearch(e.target.value)} placeholder="Search by name, label or section…" autoFocus />
              <b>{filteredLibrary.length} visuals</b>
            </div>
            <div className="adm-asset-picker-grid">
              {libraryLoading && <div className="adm-asset-picker-empty">Opening your visual archive…</div>}
              {!libraryLoading && filteredLibrary.length === 0 && <div className="adm-asset-picker-empty">No matching images found.</div>}
              {!libraryLoading && filteredLibrary.map(item => (
                <button key={item.id} className="adm-asset-tile" onClick={() => chooseLibraryImage(item)} disabled={saving}>
                  <img src={item.url} alt={item.customLabel || item.filename || "Library visual"} loading="lazy" />
                  <span className="adm-asset-tile-overlay"><b>Use this image</b><i>→</i></span>
                  <span className="adm-asset-tile-meta">
                    <strong>{item.customLabel || item.filename || "Untitled image"}</strong>
                    <small>{item.slot === "other" ? "Media Library" : (item.slot || "Media Library").replaceAll("-", " ")}</small>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
